import { z } from 'zod';
import { CursorQuerySchema, OpaqueCursorSchema, PayformCustomerSummarySchema, PaymentCurrencyCodeSchema, createCursorPageSchema, } from './customers';
import { IsoTimestampSchema, StripeCustomerIdSchema } from './primitives';
import { PointPaySaleIdSchema } from '../types/sale/sale-links';
/** Navigation context only. The server must re-authorize the retained linkage. */
export const PointPayAccountContextSchema = OpaqueCursorSchema;
export const PointPayAccountQuerySchema = z
    .object({
    accountContext: PointPayAccountContextSchema.optional(),
})
    .strict();
export const PointPayPaymentIdSchema = z
    .string()
    .max(255)
    .regex(/^(pi|ch)_[A-Za-z0-9]+$/);
export const PointPayPaymentParamsSchema = z
    .object({ paymentId: PointPayPaymentIdSchema })
    .strict();
export const PointPayCustomerParamsSchema = z
    .object({ customerId: StripeCustomerIdSchema })
    .strict();
export const PointPayPaymentStatusSchema = z.enum([
    'SUCCEEDED',
    'PROCESSING',
    'REQUIRES_PAYMENT_METHOD',
    'REQUIRES_CONFIRMATION',
    'REQUIRES_ACTION',
    'REQUIRES_CAPTURE',
    'FAILED',
    'CANCELED',
    'UNKNOWN',
]);
/** Failure needs evidence; a new intent needing a method is incomplete. */
export function normalizePointPayPaymentStatus(status, failedAttempt = false) {
    if (status === 'requires_payment_method' && failedAttempt)
        return 'FAILED';
    const parsed = PointPayPaymentStatusSchema.safeParse(status.toUpperCase());
    return parsed.success ? parsed.data : 'UNKNOWN';
}
const MinorAmount = z.number().int().nonnegative().safe();
export const PointPayMethodSchema = z
    .object({
    type: z.string().max(80),
    brand: z.string().max(40).nullable(),
    last4: z
        .string()
        .regex(/^\d{4}$/)
        .nullable(),
})
    .strict();
export const PointPayRefundStatusSchema = z.enum([
    'SUCCEEDED',
    'PENDING',
    'REQUIRES_ACTION',
    'FAILED',
    'CANCELED',
    'UNKNOWN',
]);
export const PointPayRefundSchema = z
    .object({
    id: z.string().max(255),
    createdAt: IsoTimestampSchema,
    amountMinor: MinorAmount,
    currency: PaymentCurrencyCodeSchema,
    status: PointPayRefundStatusSchema,
})
    .strict();
export const PointPayRefundSummarySchema = z
    .object({
    state: z.enum(['NONE', 'PARTIAL', 'FULL', 'UNKNOWN']),
    /** Null when the bounded refund evidence cannot establish a complete total. */
    succeededMinor: MinorAmount.nullable(),
    pending: z.boolean(),
    failed: z.boolean(),
    complete: z.boolean(),
})
    .strict();
export const PointPayDisputeSchema = z
    .object({
    state: z.enum(['NONE', 'OPEN', 'WON', 'LOST', 'CLOSED', 'UNKNOWN']),
})
    .strict();
export const PointPayPaymentCustomerSchema = z
    .object({
    customerId: StripeCustomerIdSchema,
    name: z.string().max(200).nullable(),
    email: z.string().max(320).nullable(),
    deleted: z.boolean(),
})
    .strict();
export const PointPayPaymentSummarySchema = z
    .object({
    paymentId: PointPayPaymentIdSchema,
    kind: z.enum(['PAYMENT_INTENT', 'CHARGE']),
    createdAt: IsoTimestampSchema,
    status: PointPayPaymentStatusSchema,
    /** Collected amount for succeeded payments, requested amount otherwise. */
    amountMinor: MinorAmount,
    requestedMinor: MinorAmount,
    receivedMinor: MinorAmount,
    currency: PaymentCurrencyCodeSchema,
    currencyExponent: z.number().int().min(0).max(3),
    customer: PointPayPaymentCustomerSchema.nullable(),
    customerVisibility: z.enum([
        'VISIBLE',
        'GUEST',
        'RESTRICTED',
        'UNAVAILABLE',
    ]),
    method: PointPayMethodSchema.nullable(),
    refund: PointPayRefundSummarySchema,
    dispute: PointPayDisputeSchema,
    /** Only an existing, verified same-business Sales.id, never a payment id. */
    saleId: PointPaySaleIdSchema.nullable(),
})
    .strict();
const Context = {
    accountContext: PointPayAccountContextSchema,
    readAt: IsoTimestampSchema,
};
export const PointPayPaymentsResponseSchema = z
    .object({
    ...Context,
    page: createCursorPageSchema(PointPayPaymentSummarySchema),
    coverage: z.literal('PAYMENT_INTENTS_AND_CHARGE_ONLY'),
    /** Raw provider objects examined in this bounded continuation, not a total. */
    scanned: z.number().int().min(0).max(100),
})
    .strict();
export const PointPayPaymentDetailSchema = z
    .object({
    ...Context,
    payment: PointPayPaymentSummarySchema,
    /** Charge attempts are current provider evidence, not a historical state timeline. */
    attempts: createCursorPageSchema(PointPayPaymentSummarySchema),
    refunds: createCursorPageSchema(PointPayRefundSchema),
})
    .strict();
export const PointPayCustomersResponseSchema = z
    .object({
    ...Context,
    page: createCursorPageSchema(PayformCustomerSummarySchema),
    searchMayLag: z.boolean(),
})
    .strict();
export const PointPayCustomerDetailSchema = z
    .object({
    ...Context,
    customer: PayformCustomerSummarySchema,
})
    .strict();
export const PointPayCustomersQuerySchema = CursorQuerySchema.extend({
    ...PointPayAccountQuerySchema.shape,
    email: z.string().min(1).max(320).optional(),
    q: z.string().trim().min(2).max(100).optional(),
})
    .strict()
    .refine((v) => !(v.email && v.q), 'Choose exact email or text search');
export const PointPayPaymentsQuerySchema = CursorQuerySchema.extend({
    ...PointPayAccountQuerySchema.shape,
    customerId: StripeCustomerIdSchema.optional(),
    status: PointPayPaymentStatusSchema.optional(),
    createdFrom: z.coerce.number().int().nonnegative().safe().optional(),
    createdTo: z.coerce.number().int().nonnegative().safe().optional(),
})
    .strict()
    .refine((v) => v.createdFrom === undefined ||
    v.createdTo === undefined ||
    v.createdFrom <= v.createdTo, 'Invalid date range');
export const PointPayPaymentDetailQuerySchema = PointPayAccountQuerySchema.extend({
    attemptCursor: OpaqueCursorSchema.optional(),
    refundCursor: OpaqueCursorSchema.optional(),
}).strict();
/** Stripe charge units differ from display currency units for ISK and UGX. */
export function pointPayCurrencyExponent(currency) {
    if (['ISK', 'UGX'].includes(currency.toUpperCase()))
        return 2;
    return new Intl.NumberFormat('en', {
        style: 'currency',
        currency,
    }).resolvedOptions().maximumFractionDigits;
}
export function formatPointPayMoney(amountMinor, currency, exponent, locale = 'en-US') {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amountMinor / 10 ** exponent);
}
/** Attached methods only. Visibility does not establish permission to charge. */
export const PointPaySavedMethodSchema = z
    .object({
    methodId: z
        .string()
        .max(255)
        .regex(/^pm_[A-Za-z0-9]+$/),
    type: z.string().max(80),
    label: z.string().max(80),
    brand: z.string().max(40).nullable(),
    last4: z
        .string()
        .regex(/^\d{4}$/)
        .nullable(),
    expiryMonth: z.number().int().min(1).max(12).nullable(),
    expiryYear: z.number().int().min(2000).max(9999).nullable(),
    isInvoiceDefault: z.boolean(),
    eligibility: z.enum([
        'AUTHORIZATION_REQUIRED',
        'EXPIRED',
        'UNSUPPORTED',
        'RESTRICTED',
    ]),
})
    .strict();
export const PointPayMethodsQuerySchema = CursorQuerySchema.extend({
    ...PointPayAccountQuerySchema.shape,
}).strict();
export const PointPayMethodsResponseSchema = z
    .object({
    merchantAttestationAllowed: z.boolean(),
    ...Context,
    page: createCursorPageSchema(PointPaySavedMethodSchema),
})
    .strict();
export const PointPayCustomerPaymentRequestSchema = z
    .object({
    requestId: z.string().uuid(),
    accountContext: PointPayAccountContextSchema,
    methodId: PointPaySavedMethodSchema.shape.methodId,
    amountMinor: z.number().int().min(50).max(99999999),
    currency: z.literal('USD'),
    description: z.string().trim().min(1).max(200),
    authorization: z.discriminatedUnion('kind', [
        z
            .object({
            kind: z.literal('MERCHANT_ATTESTATION'),
            attested: z.literal(true),
            /** Merchant's reference to permission for this exact amount and method. */
            reference: z.string().trim().min(3).max(200),
        })
            .strict(),
        z.object({ kind: z.literal('CUSTOMER_CONFIRMATION') }).strict(),
    ]),
})
    .strict();
export const PointPayCustomerPaymentStatusSchema = z.enum([
    'PAID',
    'PROCESSING',
    'CUSTOMER_CONFIRMATION_REQUIRED',
    'DECLINED',
    'CANCELED',
    'UNABLE_TO_VERIFY',
]);
export const PointPayCustomerPaymentParamsSchema = PointPayCustomerParamsSchema.extend({
    requestId: z.string().uuid(),
}).strict();
export const PointPayCustomerPaymentSchema = z
    .object({
    requestId: z.string().uuid(),
    customerId: StripeCustomerIdSchema,
    accountContext: PointPayAccountContextSchema,
    status: PointPayCustomerPaymentStatusSchema,
    amountMinor: MinorAmount,
    currency: z.literal('USD'),
    description: z.string().max(200),
    method: PointPaySavedMethodSchema,
    paymentId: PointPayPaymentIdSchema.nullable(),
    saleId: PointPaySaleIdSchema.nullable(),
    accounting: z.enum(['PENDING', 'RECORDED']),
    createdAt: IsoTimestampSchema,
})
    .strict();
/** Link issuance is explicit; ordinary merchant lists contain no guest credential. */
export const PointPayConfirmationLinkSchema = z
    .object({ url: z.string().url() })
    .strict();
export const PointPayConfirmationTokenSchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]{43}$/);
export const PointPayConfirmationParamsSchema = z
    .object({
    token: PointPayConfirmationTokenSchema,
})
    .strict();
export const PointPayConfirmationSchema = z
    .object({
    merchantName: z.string().max(200),
    status: PointPayCustomerPaymentStatusSchema,
    amountMinor: MinorAmount,
    currency: z.literal('USD'),
    description: z.string().max(200),
    method: PointPayMethodSchema,
})
    .strict();
export const PointPayConfirmationStartSchema = z
    .object({
    accepted: z.literal(true),
    recaptchaToken: z.string().min(1).max(8192),
})
    .strict();
/** Only returned to the payment-scoped capability after explicit acceptance. */
export const PointPayIntentConfirmationSchema = z
    .object({
    kind: z.literal('PAYMENT_INTENT'),
    paymentMethodId: PointPaySavedMethodSchema.shape.methodId,
    publishableKey: z.string().regex(/^pk_(test|live)_/),
    connectedAccountId: z.string().regex(/^acct_[A-Za-z0-9]+$/),
    clientSecret: z.string().regex(/^pi_[A-Za-z0-9]+_secret_[A-Za-z0-9]+$/),
})
    .strict();
